/**
 * AllCombosCard — 1-click "play every yesterday's winner as a combo".
 * Pulls hot-list suggestions from /api/lottery/hot-numbers (defaults to the
 * previous-day winners across the selected states), lets the player tick
 * individual numbers on/off, and bulk-places combo (ways) tickets at the
 * chosen amount via /api/lottery/combos-bulk.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Layers, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATE_COLORS = { TN: '#FF6B35', IL: '#39FF14', MS: '#FFD700' };

// Count unique permutations for a digit list (e.g. [1,2,3] → 6, [1,1,2] → 3)
const factorial = (n) => { let r = 1; for (let i = 2; i <= n; i++) r *= i; return r; };
const computeWays = (digits) => {
    if (!digits || digits.length === 0) return 0;
    const counts = {};
    digits.forEach((d) => { counts[d] = (counts[d] || 0) + 1; });
    let denom = 1;
    Object.values(counts).forEach((c) => { denom *= factorial(c); });
    return factorial(digits.length) / denom;
};

const AllCombosCard = ({ allowedStates = ['TN', 'IL', 'MS'], onPurchased }) => {
    const [pickType] = useState(3);
    const [days, setDays] = useState(1);                          // hot-list lookback
    const [time, setTime] = useState('evening');                  // midday | evening
    const [statesPicked, setStatesPicked] = useState(() => new Set(allowedStates));
    const [amount, setAmount] = useState('1');
    const [hot, setHot] = useState({ numbers: [], drawn_in: {} });
    const [selected, setSelected] = useState(() => new Set());    // keys = sorted digit-string
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const keyOf = (nums) => [...nums].sort().join(',');

    const fetchHot = useCallback(async () => {
        if (statesPicked.size === 0) { setHot({ numbers: [], drawn_in: {} }); return; }
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/lottery/hot-numbers?pick_type=${pickType}&days=${days}&states=${Array.from(statesPicked).join(',')}`,
                { withCredentials: true }
            );
            setHot(data);
            // Auto-select all by default — player can untick individuals
            setSelected(new Set((data.numbers || []).map(keyOf)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load hot numbers');
        } finally { setLoading(false); }
    }, [days, pickType, statesPicked]);

    useEffect(() => { fetchHot(); }, [fetchHot]);

    const toggle = (nums) => {
        const k = keyOf(nums);
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };
    const toggleState = (s) => setStatesPicked((prev) => {
        const next = new Set(prev);
        if (next.has(s)) next.delete(s); else next.add(s);
        return next;
    });

    // Pick out the selected numbers (as arrays) for cost calc + submit
    const selectedNums = hot.numbers.filter((n) => selected.has(keyOf(n)));
    const amtFloat = parseFloat(amount || 0);
    const totalPerms = selectedNums.reduce((sum, n) => sum + computeWays(n), 0);
    const totalCost = (amtFloat * totalPerms * statesPicked.size) || 0;

    const submit = async () => {
        if (!amtFloat || amtFloat <= 0) { toast.error('Enter a wager amount'); return; }
        if (statesPicked.size === 0) { toast.error('Pick at least one state'); return; }
        if (selectedNums.length === 0) { toast.error('Tick at least one number'); return; }
        if (!window.confirm(
            `Place ${selectedNums.length} combo ticket(s) on ${Array.from(statesPicked).join(', ')} (${time}) drawing?\n\n` +
            `$${amtFloat.toFixed(2)} per permutation × ${totalPerms} perms × ${statesPicked.size} state(s) = $${totalCost.toFixed(2)} total.`
        )) return;
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/lottery/combos-bulk`, {
                numbers: selectedNums,
                states: Array.from(statesPicked),
                pick_type: pickType,
                amount: amtFloat,
                drawing_time: time,
            }, { withCredentials: true });
            toast.success(
                `Placed ${data.combos_placed} combo(s) — ${data.ids.length} tickets · $${data.total_cost.toFixed(2)}`
            );
            if (onPurchased) onPurchased(data);
            fetchHot();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'All Combos failed');
        } finally { setSubmitting(false); }
    };

    return (
        <div className="mb-4 rounded-2xl border-2 border-[#A855F7]/40 bg-gradient-to-br from-[#A855F7]/10 to-fuchsia-900/10 overflow-hidden" data-testid="all-combos-card">
            <div className="bg-gradient-to-r from-[#A855F7]/20 to-fuchsia-700/20 px-4 py-2 border-b border-[#A855F7]/30 flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-wider text-[#A855F7] flex items-center gap-1.5">
                    <Layers className="w-4 h-4" /> All Combos
                </h3>
                <span className="text-[9px] uppercase tracking-wider text-fuchsia-200/80">
                    yesterday's winners · combo rate
                </span>
            </div>
            <div className="p-3">
                {/* States */}
                <div className="flex items-center justify-center gap-2 mb-3" data-testid="all-combos-states">
                    {allowedStates.map((st) => {
                        const active = statesPicked.has(st);
                        return (
                            <button key={st} type="button" onClick={() => toggleState(st)}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition border ${
                                    active
                                        ? 'text-[#050505] border-transparent'
                                        : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                                }`}
                                style={active ? { backgroundColor: STATE_COLORS[st] || '#A855F7' } : undefined}
                                data-testid={`all-combos-state-${st}`}>
                                {st}
                            </button>
                        );
                    })}
                </div>

                {/* Drawing time + lookback */}
                <div className="flex items-center justify-center gap-2 mb-3 flex-wrap" data-testid="all-combos-time">
                    {['midday', 'evening'].map((t) => (
                        <button key={t} type="button" onClick={() => setTime(t)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition border ${
                                time === t ? 'bg-[#FFD700] text-[#050505] border-transparent' : 'bg-zinc-900 text-zinc-400 border-zinc-700'
                            }`}
                            data-testid={`all-combos-time-${t}`}>
                            {t}
                        </button>
                    ))}
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                        className="bg-zinc-900 text-zinc-300 border border-zinc-700 rounded px-2 py-1 text-[11px]"
                        data-testid="all-combos-lookback">
                        <option value={1}>Yesterday</option>
                        <option value={3}>Last 3 days</option>
                        <option value={7}>Last 7 days</option>
                    </select>
                    <button type="button" onClick={fetchHot} disabled={loading}
                        className="text-zinc-400 hover:text-white disabled:opacity-50"
                        data-testid="all-combos-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Amount + summary */}
                <div className="flex items-center justify-between gap-2 mb-3 text-[11px] text-zinc-300 flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="text-zinc-500">Per perm:</span>
                        <select value={amount} onChange={(e) => setAmount(e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1"
                            data-testid="all-combos-amount">
                            {['0.50', '1', '2', '5', '10'].map((v) => (
                                <option key={v} value={v}>${v}</option>
                            ))}
                        </select>
                    </div>
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Total: </span>
                        <span className="text-white font-mono font-black">${totalCost.toFixed(2)}</span>
                        <span className="text-zinc-500"> · {totalPerms} perms × {statesPicked.size} state{statesPicked.size === 1 ? '' : 's'}</span>
                    </div>
                </div>

                {/* Hot-list chips */}
                {loading ? (
                    <div className="text-center py-4 text-zinc-500 text-xs">
                        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading hot numbers…
                    </div>
                ) : hot.numbers.length === 0 ? (
                    <p className="text-center text-zinc-500 text-xs py-3">
                        No combo-eligible winners in the lookback window. Try expanding to 3 or 7 days.
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-1.5 mb-3 justify-center" data-testid="all-combos-chips">
                            {hot.numbers.map((n) => {
                                const k = keyOf(n);
                                const active = selected.has(k);
                                const ways = computeWays(n);
                                const sources = (hot.drawn_in?.[k] || []).join('; ');
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => toggle(n)}
                                        title={sources}
                                        className={`px-2 py-1 rounded-md text-[11px] font-bold tracking-wider border transition ${
                                            active
                                                ? 'bg-[#A855F7]/20 text-[#A855F7] border-[#A855F7]/60'
                                                : 'bg-zinc-900 text-zinc-500 border-zinc-700 hover:border-zinc-500'
                                        }`}
                                        data-testid={`all-combos-chip-${k}`}
                                    >
                                        {n.join('-')}
                                        <span className="opacity-70 ml-1">({ways}w)</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex gap-2 mb-3 justify-center">
                            <button type="button" onClick={() => setSelected(new Set(hot.numbers.map(keyOf)))}
                                className="text-[10px] text-zinc-400 underline" data-testid="all-combos-select-all">
                                Select all
                            </button>
                            <button type="button" onClick={() => setSelected(new Set())}
                                className="text-[10px] text-zinc-400 underline" data-testid="all-combos-clear">
                                Clear
                            </button>
                        </div>
                    </>
                )}

                <Button onClick={submit}
                    disabled={submitting || selectedNums.length === 0 || statesPicked.size === 0}
                    className="w-full bg-gradient-to-r from-[#A855F7] to-fuchsia-500 hover:from-[#A855F7]/90 hover:to-fuchsia-500/90 text-white font-black text-sm py-2.5 disabled:opacity-50"
                    data-testid="play-all-combos-btn">
                    {submitting
                        ? <><Loader2 className="w-4 h-4 mr-1 animate-spin inline" /> Placing…</>
                        : selectedNums.length === 0
                            ? 'Tick at least one combo'
                            : `PLAY ${selectedNums.length} COMBO${selectedNums.length === 1 ? '' : 'S'} — $${totalCost.toFixed(2)}`}
                </Button>
            </div>
        </div>
    );
};

export default AllCombosCard;
